'use client';

// Vercel Analytics configuration
import { Analytics } from '@vercel/analytics/react';
// import { SpeedInsights } from '@vercel/speed-insights/react'
import React from 'react';

/**
 * Vercel Analytics is exempt from cookie consent requirements because:
 * 1. It's first-party analytics that doesn't collect or share personal data
 * 2. It's considered essential for measuring site performance and improving user experience
 * 3. It doesn't use cookies for tracking or require cookie storage
 *
 * This implementation intentionally bypasses any cookie consent checks
 * to ensure we always have basic analytics data for site improvement.
 */

// Check if Vercel Analytics is enabled based on environment
export const isVercelAnalyticsEnabled = () => {
  // Only enable in production by default, but allow override with env var
  return process.env.NEXT_PUBLIC_ENABLE_VERCEL_ANALYTICS === 'true' || process.env.NODE_ENV === 'production' && process.env.NEXT_PUBLIC_DISABLE_VERCEL_ANALYTICS !== 'true';
};

// Enhanced Analytics component for use in Client Components with conditional rendering
export function VercelAnalytics() {
  const isEnabled = isVercelAnalyticsEnabled();
  if (!isEnabled) {
    return null;
  }
  return <>
      <Analytics mode="auto" debug={process.env.NODE_ENV !== 'production'} beforeSend={event => {
      // Add custom data to events to make user tracking more accurate
      return {
        ...event,
        // Add session timestamp to help with session tracking
        ts: Date.now(),
        // Include a user session ID to better track unique users
        usid: getOrCreateSessionId(),
        // Add reference to indicate full session tracking
        full_tracking: true
      };
    }} data-sentry-element="Analytics" data-sentry-source-file="vercel.tsx" />
      {/* Disabled SpeedInsights due to 404 errors */}
      {/* <SpeedInsights /> */}
    </>;
}

// Helper function to check if we're in a browser environment
export const isBrowser = () => {
  return typeof window !== 'undefined';
};

// Helper to get or create a session ID to help with user tracking
const getOrCreateSessionId = () => {
  if (!isBrowser()) {
    return undefined;
  }
  let sessionId;
  try {
    // Try to get existing session ID
    sessionId = sessionStorage.getItem('vercel_analytics_sid');

    // If no session ID exists, create a new one
    if (!sessionId) {
      sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 12)}`;
      sessionStorage.setItem('vercel_analytics_sid', sessionId);
    }
  } catch (e) {
    // Handle SecurityError or any other exceptions when accessing sessionStorage
    console.error('Failed to access or save analytics session ID:', e);
    // Generate a temporary session ID but don't attempt to store it
    sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 12)}-temp`;
  }
  return sessionId;
};

// This standalone component can be used for explicit inclusion in your layout
export function VercelAnalyticsComponent() {
  return <VercelAnalytics data-sentry-element="VercelAnalytics" data-sentry-component="VercelAnalyticsComponent" data-sentry-source-file="vercel.tsx" />;
}